<template>
  <div class="style-template-metadata">
    <div
      slot="body"
      class="style-template-metadata__body"
    >
      <input
        id="name"
        v-model.trim="name"
        class="style-template-metadata__name"
        data-testid="name"
        type="text"
        placeholder="Name"
        @blur="changeStyleName"
      >

      <ElSelect
        v-for="(filterGroup, key) in {
          ...styleMetaFilters,
          ...styleHubFilters
        }"
        :key="key"
        v-model="meta[key]"
        class="style-template-metadata__select"
        :placeholder="filterGroup.name"
        size="mini"
        filterable
        clearable
        no-match-text="No match"
        @change="value => onFilterChange(key, value)"
      >
        <ElOption
          v-for="value in filterGroup.values"
          :key="typeof value === 'object' ? value.id : value"
          :value="typeof value === 'object' ? value.id : value"
          :label="typeof value === 'object' ? value.name : value"
        />
      </ElSelect>

      <TagsEditor
        :value="tagStrings"
        :suggestions="tagSuggestionStrings"
        @add="assignTagToStyle"
        @remove="unassignTagFromStyle"
      />

      <ImagePreview
        :image-url="getImageUrlTrimmed(style.assets_label)"
        can-be-deleted
        placeholder="Image Label"
        @image-change="onImageChange"
        @image-delete="onImageDelete"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { imageResolutions } from '@/constants/image'
import ImagePreview from '@/components/ImagePreview.vue'
import TagsEditor from '@/components/TagsEditor'

import { Workspace } from '@/mixins/workspace'
import { Projects } from '@/mixins/projects'
import { S3Trim } from '@/mixins/s3Trim'
import Utils from '@/services/utils'
import { styleMetaKeys } from '@/constants/meta'

export default {
  name: 'StyleTemplateMetadata',

  components: {
    ImagePreview,
    TagsEditor
  },

  mixins: [Workspace, Projects, S3Trim],

  props: {
    styleId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    name: null,
    meta: {}
  }),

  computed: {
    ...mapGetters([
      'getStyleById',
      'getCustomTags',
      'getStyleMetaFilters',
      'getStyleHubFilters'
    ]),

    /**
     * @returns {object}
     */
    style () {
      return this.getStyleById(this.styleId)
    },

    /**
     * @returns {string}
     */
    tagStrings () {
      return this.style.custom_tags.map(tag => tag.text)
    },

    /**
     * @returns {string[]}
     */
    tagSuggestionStrings () {
      return this.getCustomTags(
        this.selectedGroupId,
        this.selectedSeasonId
      ).map(suggestion => suggestion.text)
    },

    /**
     * @returns {object}
     */
    styleMetaFilters () {
      return this.getStyleMetaFilters
    },

    /**
     * @returns {object}
     */
    styleHubFilters () {
      return this.getStyleHubFilters
    }
  },

  watch: {
    /**
     */
    style () {
      this.setInitialData()
    }
  },

  async created () {
    if (this.selectedGroupId && this.selectedSeasonId) {
      this.fetchCustomTags({
        groupId: this.selectedGroupId,
        seasonId: this.selectedSeasonId
      })
    }

    await this.fetchStyleMetaFilters()
    await this.fetchStyleHubFilters()

    this.setInitialData()
  },

  methods: {
    ...mapActions([
      'fetchCustomTags',
      'fetchStyleMetaFilters',
      'fetchStyleHubFilters'
    ]),

    /**
     */
    setInitialData () {
      this.name = this.style.name
      this.meta = Utils.parseStyleMeta(this.style)
    },

    /**
     * @param   {string} imageUrl
     *
     * @returns {string}
     */
    getImageUrlTrimmed (imageUrl) {
      return this.s3Trim(imageUrl, imageResolutions.LABEL_THUMBNAIL)
    },

    /**
     */
    changeStyleName () {
      if (this.name === '') {
        this.name = this.style.name
      }

      this.emitUpdateStyleMetadata({
        name: this.name
      })
    },

    /**
     * @param {string} name
     * @param {number} value
     */
    onFilterChange (name, value) {
      const isAttribute = !Object.values(styleMetaKeys).includes(name)
      const finalValue = value === '' ? null : value

      this.emitUpdateStyleMetadata({
        [isAttribute ? 'attributes' : 'meta']: {
          [name]: finalValue
        }
      })
    },

    /**
     * @param {object} imageChangeEvent
     */
    onImageChange (imageChangeEvent) {
      this.emitUpdateStyleMetadata({ label: imageChangeEvent.image.file })
    },

    /**
     * Updates style label sending an empty string (null doesn't work on this endpoint), overriding the image URL
     */
    onImageDelete () {
      this.emitUpdateStyleMetadata({ label: '' })
    },

    /**
     * @param {string} text
     */
    assignTagToStyle (text) {
      this.emitUpdateStyleMetadata({ newTagText: text })
    },

    /**
     * @param {string} text
     */
    unassignTagFromStyle (text) {
      const tag = this.style.custom_tags.find(tag => tag.text === text)

      this.emitUpdateStyleMetadata({ tagIdToRemove: tag.id })
    },

    /**
     * @param {object} payload
     */
    emitUpdateStyleMetadata (payload) {
      this.$emit('update-style-metadata', {
        ...payload,
        styleId: this.styleId
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.style-template-metadata {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  font-weight: $font-weight-medium;
}

.style-template-metadata__body {
  display: flex;
  flex-direction: column;
  text-align: left;

  input {
    margin-top: spacing(0.5);
    padding: 0 0 0 $spacer-unit;
    text-align: left;
    border: $border-size solid $grey-extra-light;
    border-radius: $border-radius-default;
  }

  > * {
    margin-bottom: $spacer-unit-x2;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.style-template-metadata__confirm-button {
  width: 100%;
}
</style>
